import { type ReactNode, useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router';
import { cn } from './format';
import { Icon, type IconName } from './icons';
import { Drawer } from './overlay';
import { useTheme } from './theme';

export interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  /** Yalnızca tam eşleşmede aktif (ör. ana sayfa). */
  end?: boolean;
  badge?: ReactNode;
}

export interface NavSection {
  title?: string;
  items: NavItem[];
}

export interface ShellBrand {
  name: string;
  logoUrl?: string | null;
  logoDarkUrl?: string | null;
}

export function BrandMark({ brand, className }: { brand: ShellBrand; className?: string }) {
  const { preset } = useTheme();
  const logo = preset !== 'light' ? (brand.logoDarkUrl ?? brand.logoUrl) : (brand.logoUrl ?? brand.logoDarkUrl);
  if (logo) return <img src={logo} alt={brand.name} className={cn('h-8 w-auto max-w-[160px] object-contain', className)} />;
  return (
    <span className={cn('flex min-w-0 items-center gap-2', className)}>
      <span className="flex size-8 shrink-0 items-center justify-center rounded-[calc(var(--radius)*0.75)] bg-primary text-sm font-bold text-primary-foreground">
        {brand.name.trim().charAt(0).toUpperCase() || '•'}
      </span>
      <span className="truncate font-semibold">{brand.name}</span>
    </span>
  );
}

function NavList({ sections, onNavigate }: { sections: NavSection[]; onNavigate?: () => void }) {
  return (
    <nav className="flex flex-col gap-4" aria-label="Ana menü">
      {sections
        .filter((s) => s.items.length)
        .map((s, i) => (
          <div key={s.title ?? i}>
            {s.title ? <div className="mb-1 px-3 text-[11px] font-semibold tracking-wider text-muted uppercase">{s.title}</div> : null}
            <ul className="flex flex-col gap-0.5">
              {s.items.map((it) => (
                <li key={it.to}>
                  <NavLink
                    to={it.to}
                    end={it.end}
                    onClick={onNavigate}
                    className={({ isActive }) =>
                      cn(
                        'flex min-h-10 items-center gap-3 rounded-[calc(var(--radius)*0.75)] px-3 text-sm transition',
                        isActive ? 'bg-primary/10 font-medium text-primary' : 'text-foreground/80 hover:bg-surface hover:text-foreground',
                      )
                    }
                  >
                    <Icon name={it.icon} />
                    <span className="min-w-0 flex-1 truncate">{it.label}</span>
                    {it.badge}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
    </nav>
  );
}

/**
 * Uygulama iskeleti. Masaüstü: sabit sol menü. Mobil: üst çubuk + çekmece menü +
 * alt sekme çubuğu (`mobileTabs`, en fazla 4; beşincisi "Menü").
 */
export function AppShell({
  brand,
  nav,
  mobileTabs,
  topBar,
  sidebarFooter,
  banner,
  strip,
  children,
}: {
  brand: ShellBrand;
  nav: NavSection[];
  mobileTabs?: NavItem[];
  topBar?: ReactNode;
  sidebarFooter?: ReactNode;
  banner?: ReactNode;
  /** Header'ın alt kenarına yapışık ince durum şeridi (ör. seans durumu). */
  strip?: ReactNode;
  children: ReactNode;
}) {
  const [drawer, setDrawer] = useState(false);
  const location = useLocation();
  useEffect(() => setDrawer(false), [location.pathname]);
  const tabs = (mobileTabs ?? []).slice(0, 4);

  return (
    <div className="min-h-dvh bg-background">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col border-r border-border bg-card lg:flex">
        <div className="flex h-16 shrink-0 items-center border-b border-border px-4">
          <BrandMark brand={brand} />
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-3">
          <NavList sections={nav} />
        </div>
        {sidebarFooter ? <div className="border-t border-border p-3">{sidebarFooter}</div> : null}
      </aside>

      <Drawer open={drawer} onClose={() => setDrawer(false)}>
        <div className="flex h-14 shrink-0 items-center justify-between border-b border-border px-4">
          <BrandMark brand={brand} />
          <button type="button" onClick={() => setDrawer(false)} className="rounded p-2 text-muted hover:bg-surface" aria-label="Menüyü kapat">
            <Icon name="x" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-3">
          <NavList sections={nav} onNavigate={() => setDrawer(false)} />
        </div>
        {sidebarFooter ? <div className="border-t border-border p-3">{sidebarFooter}</div> : null}
      </Drawer>

      <div className="flex min-h-dvh flex-col lg:pl-64">
        {banner}
        <header className="pt-safe sticky top-0 z-20 border-b border-border bg-card/90 backdrop-blur">
          <div className="flex h-14 items-center gap-2 px-3 md:h-16 md:px-6">
            <button type="button" onClick={() => setDrawer(true)} className="rounded p-2 text-foreground hover:bg-surface lg:hidden" aria-label="Menüyü aç">
              <Icon name="menu" size={20} />
            </button>
            <div className="min-w-0 lg:hidden">
              <BrandMark brand={brand} />
            </div>
            <div className="ml-auto flex items-center gap-1 md:gap-2">{topBar}</div>
          </div>
          {strip}
        </header>
        <main className={cn('mx-auto w-full max-w-[1400px] flex-1 px-3 py-4 md:px-6 md:py-6', tabs.length && 'pb-24 lg:pb-6')}>{children}</main>
      </div>

      {tabs.length ? (
        <nav className="pb-safe fixed inset-x-0 bottom-0 z-30 border-t border-border bg-card/95 backdrop-blur lg:hidden" aria-label="Hızlı menü">
          <ul className="grid" style={{ gridTemplateColumns: `repeat(${tabs.length + 1}, minmax(0, 1fr))` }}>
            {tabs.map((t) => (
              <li key={t.to}>
                <NavLink
                  to={t.to}
                  end={t.end}
                  className={({ isActive }) => cn('flex min-h-14 flex-col items-center justify-center gap-0.5 text-[11px]', isActive ? 'text-primary' : 'text-muted')}
                >
                  <Icon name={t.icon} size={20} />
                  <span className="max-w-full truncate px-1">{t.label}</span>
                </NavLink>
              </li>
            ))}
            <li>
              <button type="button" onClick={() => setDrawer(true)} className="flex min-h-14 w-full flex-col items-center justify-center gap-0.5 text-[11px] text-muted">
                <Icon name="menu" size={20} />
                <span>Menü</span>
              </button>
            </li>
          </ul>
        </nav>
      ) : null}
    </div>
  );
}

/** Giriş, şifre sıfırlama gibi oturumsuz sayfalar için ortalanmış kart düzeni. */
export function AuthLayout({ brand, title, description, children, footer }: { brand: ShellBrand; title: ReactNode; description?: ReactNode; children: ReactNode; footer?: ReactNode }) {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center bg-background px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex justify-center">
          <BrandMark brand={brand} className="[&_img]:h-10" />
        </div>
        <div className="rounded-[var(--radius)] border border-border bg-card p-5 shadow-sm sm:p-6">
          <h1 className="text-lg font-semibold">{title}</h1>
          {description ? <p className="mt-1 text-sm text-muted">{description}</p> : null}
          <div className="mt-5">{children}</div>
        </div>
        {footer ? <div className="mt-4 text-center text-sm text-muted">{footer}</div> : null}
      </div>
    </div>
  );
}

export function FullPageSpinner() {
  return (
    <div className="flex min-h-dvh items-center justify-center text-muted">
      <svg className="animate-spin" width={28} height={28} viewBox="0 0 24 24" fill="none" role="status" aria-label="Yükleniyor">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.2" strokeWidth="3" />
        <path d="M22 12a10 10 0 0 0-10-10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
      </svg>
    </div>
  );
}
